import React, {
  FC, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import Modal from 'src/common/components/Modal';
import SearchStore from 'src/search/store/SearchStore';
import Icon from 'src/common/components/Icon';
import { trackEvent } from 'src/common/3rdParty/tracking';
import { preventSwipeGesture } from 'src/common/components/RangeInput';
import getClassNames from 'src/common/utils/getClassNames';
import { GA_EVENT_FILTER } from 'src/common/gaEvents';
import useAfterRenderCallback from 'src/common/hooks/useAfterRenderCallback';
import InstabookFilter from 'src/search/components/InstabookFilter';
import SuperOwnerFilter from 'src/search/components/SuperOwnerFilter';
import Button from 'src/common/components/ButtonDS22';
import CategoriesFilter from 'src/search/components/CategoriesFilter';
import LoadingSpinner from 'src/common/components/LoadingSpinner';
import Typography from 'src/common/components/Typography';
import convertStoreToRecentSearch from 'src/common/utils/convertStoreToRecentSearch';
import { useSearchInputContext } from 'src/common/components/Search/SearchInputProvider';
import { formatDate } from 'src/common/utils/dateHelpers';
import ButtonGroup from 'src/common/components/dockyard/ButtonGroup';
import FilterModalPrice from './components/FilterModalPrice';
import FilterModalCaptain from './components/FilterModalCaptain';
import FilterModalCapacity from './components/FilterModalCapacity';
import FilterModalDatePicker from './components/FilterModalDatePicker';
import styles from './FiltersModal.module.scss';

const {
  Header, Title, Body, Footer,
} = Modal;

const GA_ACTIONS = {
  APPLIED: 'applied',
  CLEARED: 'cleared',
  CLOSED: 'closed',
  OPENED: 'opened',
};

type StoreProperty =
| 'capacity'
| 'captained'
| 'dates'
| 'instabooks'
| 'superowners'
| 'price'
| 'categories';

const stringOrUndefined = (x: number | null): string | undefined => (x != null ? `${x}` : undefined);

type FiltersModalProps = {
  searchStore: SearchStore;
  isOpen: boolean;
  onModalClose: () => void;
};

const FiltersModal: FC<FiltersModalProps> = ({
  searchStore,
  isOpen = false,
  onModalClose,
}) => {
  const [t] = useTranslation(['search-page', 'common']);
  const loadingTimeout = useRef<ReturnType<typeof setTimeout>>();
  const { addRecentSearch } = useSearchInputContext();
  const priceRangeWrapperRef = useRef<HTMLDivElement>(null);
  const [resultsCount, setResultsCount] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const hasReset = useRef(false);

  const getFromStore = useCallback(() => {
    const {
      dates: { value: dates },
      capacity: { value: capacity },
      captained: { value: captained },
      min_price: { value: minPrice },
      max_price: { value: maxPrice },
      instabooks: { value: instabooks },
      superowners: { value: superowners },
      categories: { value: categories },
      duration: { value: duration },
    } = searchStore;
    // Dates may be returned as [null]. This is intentional for other components, but makes
    // things tricker here. Makes [null]->[]
    let validDates = dates;
    if (Array.isArray(dates) && dates.length && dates[0] == null) {
      validDates = [];
    }

    return ({
      captained: captained ?? undefined,
      dates: validDates,
      capacity,
      price: [minPrice ?? null, maxPrice ?? null],
      instabooks: instabooks ?? undefined,
      superowners: superowners ?? undefined,
      categories,
      duration,
    });
  }, [searchStore]);

  const [state, setState] = useState(getFromStore());
  const [isDirty, setDirty] = useState(false);

  const preview = useAfterRenderCallback(async () => {
    const MIN_DELAY = 300;
    const startTime = Date.now();

    setDirty(false);
    setLoading(true);
    const count = await searchStore.previewFilters({
      dates: state.dates.map((date) => formatDate(date, 'yyyy-MM-dd')),
      capacity: stringOrUndefined(state.capacity),
      captained: state.captained,
      instabooks: state.instabooks,
      superowners: state.superowners,
      min_price: stringOrUndefined(state.price?.[0]),
      max_price: stringOrUndefined(state.price?.[1]),
      categories: state.categories,
    });

    setResultsCount(count);

    // Ensure that the loading state is shown for a reasonable time to avoid
    // the button flashing between states if network speed is quick
    const diffTime = Date.now() - startTime;
    if (diffTime < MIN_DELAY) {
      clearTimeout(loadingTimeout.current as any);
      loadingTimeout.current = setTimeout(
        () => setLoading(false),
        MIN_DELAY - diffTime,
      );
    } else {
      setLoading(false);
    }
  });

  const onChange = useCallback((name: StoreProperty) => (val) => {
    setState({ ...state, [name]: val });
    setDirty(true);
  }, [state]);

  const debounceApply = useAfterRenderCallback(() => {
    searchStore.triggerSearchOnFieldChange.onChange(false);
    searchStore.capacity.onChange(state.capacity);
    searchStore.onCaptainedChange(state.captained ?? null);
    searchStore.dates.onChange(state.dates);
    searchStore.min_price.onChange(state.price[0]);
    searchStore.max_price.onChange(state.price[1]);
    searchStore.instabooks.onChange(state.instabooks ?? null);
    searchStore.superowners.onChange(state.superowners ?? null);
    searchStore.categories.onChange(state.categories);
    searchStore.triggerSearchOnFieldChange.onChange(true);
    addRecentSearch(convertStoreToRecentSearch(searchStore));
  });

  const closeAndCleanup = useCallback(() => {
    onModalClose();

    hasReset.current = false;
    priceRangeWrapperRef.current?.removeEventListener('touchstart', preventSwipeGesture);
  }, [onModalClose]);

  const applyFilters = useCallback(() => {
    closeAndCleanup();
    trackEvent(GA_EVENT_FILTER, {
      p9: GA_ACTIONS.APPLIED,
    });
    debounceApply();
  }, [closeAndCleanup, debounceApply]);

  const clearFilters = useAfterRenderCallback(() => {
    setState({
      captained: null,
      dates: [],
      capacity: null,
      price: [null, null],
      instabooks: null,
      superowners: null,
      categories: [],
      duration: null,
    });
    setDirty(true);
    trackEvent(GA_EVENT_FILTER, {
      p9: GA_ACTIONS.CLEARED,
    });
    debounceApply();
  });

  const closeFilters = useCallback(() => {
    closeAndCleanup();
    trackEvent(GA_EVENT_FILTER, {
      p9: GA_ACTIONS.CLOSED,
    });
  }, [closeAndCleanup]);

  const onAfterOpen = useAfterRenderCallback(() => {
    priceRangeWrapperRef.current?.addEventListener('touchstart', preventSwipeGesture);

    trackEvent(GA_EVENT_FILTER, {
      p9: GA_ACTIONS.OPENED,
    });
  });

  useEffect(() => {
    // reset to store content in hook as onAfterOpen only resets after open animation has completed
    if (isOpen && !hasReset.current) {
      const currentState = getFromStore();
      setState(currentState);
      setResultsCount(searchStore.searchResults?.count ?? null);
      hasReset.current = true;
    }
  }, [getFromStore, isOpen, searchStore.searchResults?.count]);

  useEffect(() => () => {
    clearTimeout(loadingTimeout.current);
  }, []);

  useEffect(() => {
    if (isDirty) preview();
  }, [isDirty, preview]);

  const hasFiltersApplied = useMemo(() => Object.entries(state).some(
    ([key, val]) => {
      switch (key) {
        case 'categories':
          // Fall through
        case 'dates':
          return Array.isArray(val) && val.length;
        case 'price':
          return Array.isArray(val) && val[0] && val[1];
        default:
          return val != null;
      }
    },
  ), [state]);

  return (
    <Modal
      modalOpen={isOpen}
      label={t('filter-modal-title')}
      closeModal={closeFilters}
      classNameModifier="filters"
      closeTimeoutMS={200}
      onAfterOpen={onAfterOpen}
    >
      <Header>
        <Icon className={styles.icon} id="search/filter" />
        <Title>{t('filter-modal-title')}</Title>
      </Header>
      <Body>
        <div className={styles.instabook}>
          <InstabookFilter
            value={state.instabooks}
            onChange={onChange('instabooks')}
          />
        </div>
        <div className={styles.dates}>
          <div className={styles.filterTitle}>
            <Typography type="h3" weight="semibold" size="heading-5">
              {t('dates-filter-title')}
            </Typography>
          </div>
          <FilterModalDatePicker dates={state.dates} onChange={onChange('dates')} />
        </div>
        <div className={styles.groupedFilterWrapper}>
          <div className={styles.capacity}>
            <div className={styles.filterTitle}>
              <Typography type="h3" weight="semibold" size="heading-5">
                {t('capacity-filter-title')}
              </Typography>
            </div>
            <FilterModalCapacity
              value={state.capacity}
              onChange={onChange('capacity')}
            />
          </div>
          <div className={styles.captained}>
            <div className={styles.filterTitle}>
              <Typography type="h3" weight="semibold" size="heading-5">
                {t('captain-filter-title')}
              </Typography>
            </div>
            <FilterModalCaptain
              value={state.captained}
              onChange={onChange('captained')}
            />
          </div>
        </div>
        <div className={styles.price}>
          <div ref={priceRangeWrapperRef}>
            <div className={styles.filterTitle}>
              <Typography type="h3" weight="semibold" size="heading-5">
                {t('price-filter-title')}
              </Typography>
            </div>
            <FilterModalPrice
              values={state.price}
              onChange={onChange('price')}
              hasResults={!!searchStore.searchResults.results?.length}
            />
          </div>
        </div>
        <div className={styles.superowners}>
          <SuperOwnerFilter
            value={state.superowners}
            onChange={onChange('superowners')}
          />
        </div>
        <div className={styles.categories}>
          <div className={styles.filterTitle}>
            <Typography type="h3" weight="semibold" size="heading-5">
              {t('filter-title--categories')}
            </Typography>
          </div>
          <CategoriesFilter
            onChange={onChange('categories')}
            selectedCategories={state.categories}
          />
        </div>
      </Body>
      <Footer>
        <div className={styles.buttons}>
          <ButtonGroup apartFrom="m">
            <Button
              type="reset"
              onClick={clearFilters}
              disabled={!hasFiltersApplied}
              variant="tertiary-deemphasized"
              fitWidth="m"
              data-test="clear-filters"
            >
              <span className={getClassNames(styles, 'buttonLabel', 'reset')}>
                <span className={styles.buttonSize}>{t('filters-button--size')}</span>
                {t('filters-button--clear')}
              </span>
            </Button>
            <Button
              type="button"
              onClick={applyFilters}
              fitWidth="m"
              data-test="show-listing"
            >
              <span className={styles.buttonLabel}>
                <span className={styles.buttonSize}>{t('filters-button--size')}</span>
                {isLoading
                  ? <LoadingSpinner />
                  : t('filters-button--apply', { count: resultsCount })}
              </span>
            </Button>
          </ButtonGroup>
        </div>
      </Footer>
    </Modal>
  );
};

export default FiltersModal;
